import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { API_URLS } from '../../Services/servicioURL';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dialog } from 'primereact/dialog';
import Sidebar from '../../components/sidebar/sidebar';
import Navbar from '../../components/navbar/navbar';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

interface Tecnico {
  id: number;
  nombre: string;
  usuario: string;
}

interface Empleado {
  cveemp: string;
  nomemp: string;
}

interface Sucursal {
  id: number;
  nombre: string;
}

interface Departamento {
  cve: string;
  nombre: string;
}

interface PeticionType {
  folio: number;
  cveemp: string;
  fecha_hora: string;
  titulo: string;
  justificacion: string;
  sucursal: string;
  departamento: string;
  estatus: string;
  confirmado?: boolean;
  fechapromesa?: string | null;
  motivo?: string | null;
  tecnico_id?: number | null;
  tecnicoAsignado?: Tecnico | null;
  empleado?: Empleado;
  imagen_url?: string | null;
}

interface FormState {
  folio: string;
  empleado: Empleado | null;
  fechaHora: string;
  titulo: string;
  justificacion: string;
  sucursal: string;
  departamento: string;
  estatus: string;
  confirmado: boolean;
  fechapromesa: string | null;
  motivo: string | null;
  tecnicoAsignado: Tecnico | null;
  imagen_url: string | null;
  imagenFile?: File | null;
  imagenPreview?: string | null;
}

type Mode = 'view' | 'edit' | 'create';

const MAZATLAN_TIMEZONE = 'America/Mazatlan';
const ESTATUS_OPTIONS = [
  { label: 'PENDIENTE', value: 'PENDIENTE' },
  { label: 'TRABAJANDO', value: 'TRABAJANDO' },
  { label: 'CANCELADO', value: 'CANCELADO' },
  { label: 'LISTO', value: 'LISTO' }
];

const ESTATUS_COLORS: Record<string, string> = {
  PENDIENTE: '#ffc107', TRABAJANDO: '#28a745', CANCELADO: '#dc3545', 
  LISTO: '#17a2b8', CONFIRMADO: '#0d6efd', DEFAULT: '#6c757d'
};

const formatDateTime = (date: string): string => {
  try {
    const dateObj = new Date(date);
    return isNaN(dateObj.getTime()) ? '-' : dateObj.toLocaleString('es-ES', {
      timeZone: MAZATLAN_TIMEZONE, day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return '-'; }
};

const getCurrentDateTime = (): string => {
  try {
    const now = new Date();
    const mazatlanTime = new Date(now.toLocaleString("en-US", {timeZone: MAZATLAN_TIMEZONE}));
    return `${mazatlanTime.getFullYear()}-${String(mazatlanTime.getMonth() + 1).padStart(2, '0')}-${String(mazatlanTime.getDate()).padStart(2, '0')}T${String(mazatlanTime.getHours()).padStart(2, '0')}:${String(mazatlanTime.getMinutes()).padStart(2, '0')}`;
  } catch { return ''; }
};

const formatDateTimeForDB = (dateTimeLocal: string): string => {
  if (!dateTimeLocal) return '';
  try {
    const localDate = new Date(dateTimeLocal);
    if (isNaN(localDate.getTime())) return dateTimeLocal.replace('T', ' ');
    const mazatlanTime = new Date(localDate.toLocaleString("en-US", {timeZone: MAZATLAN_TIMEZONE}));
    return `${mazatlanTime.getFullYear()}-${String(mazatlanTime.getMonth() + 1).padStart(2, '0')}-${String(mazatlanTime.getDate()).padStart(2, '0')} ${String(mazatlanTime.getHours()).padStart(2, '0')}:${String(mazatlanTime.getMinutes()).padStart(2, '0')}:${String(mazatlanTime.getSeconds()).padStart(2, '0')}`;
  } catch { return dateTimeLocal.replace('T', ' '); }
};

const formatDateTimeFromDB = (dateTimeDB: string): string => {
  if (!dateTimeDB) return '';
  try {
    let dateStr = dateTimeDB.includes(' ') ? dateTimeDB.replace(' ', 'T') : dateTimeDB;
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/)) dateStr += ':00';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateTimeDB.includes(' ') ? dateTimeDB.replace(' ', 'T').slice(0, 16) : dateTimeDB.slice(0, 16);
    const mazatlanTime = new Date(date.toLocaleString("en-US", {timeZone: MAZATLAN_TIMEZONE}));
    return `${mazatlanTime.getFullYear()}-${String(mazatlanTime.getMonth() + 1).padStart(2, '0')}-${String(mazatlanTime.getDate()).padStart(2, '0')}T${String(mazatlanTime.getHours()).padStart(2, '0')}:${String(mazatlanTime.getMinutes()).padStart(2, '0')}`;
  } catch { return dateTimeDB.includes(' ') ? dateTimeDB.replace(' ', 'T').slice(0, 16) : dateTimeDB.slice(0, 16); }
};

const getEstatusReal = (estatus: string, confirmado: boolean): string => confirmado ? 'CONFIRMADO' : estatus;
const getEstatusColor = (estatus: string): string => ESTATUS_COLORS[estatus] || ESTATUS_COLORS.DEFAULT;

const PeticionesAdmin: React.FC = () => {
  const toast = useRef<Toast>(null);
  const formRef = useRef<HTMLDivElement>(null);
  
  const [peticiones, setPeticiones] = useState<PeticionType[]>([]);
  const [tecnicos, setTecnicos] = useState<Tecnico[]>([]);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [empleados, setEmpleados] = useState<Empleado[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [mode, setMode] = useState<Mode>('view');
  const [peticionSeleccionada, setPeticionSeleccionada] = useState<PeticionType | null>(null);
  const [modalEmpleadoVisible, setModalEmpleadoVisible] = useState<boolean>(false);
  const [searchEmpleado, setSearchEmpleado] = useState<string>('');
  const [modalImagenVisible, setModalImagenVisible] = useState<boolean>(false);
  const [imagenSeleccionada, setImagenSeleccionada] = useState<string>('');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const [formState, setFormState] = useState<FormState>({
    folio: '', empleado: null, fechaHora: '', titulo: '', justificacion: '',
    sucursal: '', departamento: '', estatus: '', confirmado: false,
    fechapromesa: '', motivo: '', tecnicoAsignado: null, imagen_url: null,
    imagenFile: null, imagenPreview: null
  });

  const [filterState, setFilterState] = useState({
    searchTerm: '', estatus: '', sucursal: '', tecnico: ''
  });

  const updateFormState = useCallback((updates: Partial<FormState>) => {
    setFormState(prev => ({ ...prev, ...updates }));
  }, []);

  const resetFormState = useCallback(() => {
    setFormState({
      folio: '', empleado: null, fechaHora: '', titulo: '', justificacion: '',
      sucursal: '', departamento: '', estatus: '', confirmado: false,
      fechapromesa: '', motivo: '', tecnicoAsignado: null, imagen_url: null,
      imagenFile: null, imagenPreview: null
    });
  }, []);

  const updateFilter = useCallback((updates: Partial<typeof filterState>) => {
    setFilterState(prev => ({ ...prev, ...updates }));
  }, []);

  const resetFilters = useCallback(() => setFilterState({
    searchTerm: '', estatus: '', sucursal: '', tecnico: ''
  }), []);

  const showToast = useCallback((severity: 'success' | 'info' | 'warn' | 'error', summary: string, detail: string, life = 3000) => {
    toast.current?.show({ severity, summary, detail, life });
  }, []);

  const fetchWithErrorHandling = useCallback(async (url: string, options?: RequestInit) => {
    try {
      const response = await fetch(url, options);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error(`Error fetching ${url}:`, error);
      throw error;
    }
  }, []);

  const cargarDatosIniciales = useCallback(async () => {
    setLoading(true);
    try {
      const [peticionesRes, tecnicosRes, sucursalesRes, departamentosRes, empleadosRes] = await Promise.allSettled([
        fetchWithErrorHandling(API_URLS.peticiones.obtener),
        fetchWithErrorHandling(API_URLS.peticiones.obtenerTecnicos), // ✅ Corregido: usar la ruta específica para técnicos
        fetchWithErrorHandling(API_URLS.sucursales.obtener),
        fetchWithErrorHandling(API_URLS.departamentos.obtener),
        fetchWithErrorHandling(API_URLS.empleados.obtener)
      ]);

      setPeticiones(peticionesRes.status === 'fulfilled' && Array.isArray(peticionesRes.value) ? peticionesRes.value : []);
      
      // ✅ Extraer técnicos del response correcto
      if (tecnicosRes.status === 'fulfilled' && tecnicosRes.value?.tecnicos) {
        setTecnicos(Array.isArray(tecnicosRes.value.tecnicos) ? tecnicosRes.value.tecnicos : []);
      } else {
        setTecnicos([]);
      }
      
      setSucursales(sucursalesRes.status === 'fulfilled' && Array.isArray(sucursalesRes.value) ? sucursalesRes.value : []);
      setDepartamentos(departamentosRes.status === 'fulfilled' && Array.isArray(departamentosRes.value) ? departamentosRes.value : []);
      setEmpleados(empleadosRes.status === 'fulfilled' && Array.isArray(empleadosRes.value) ? empleadosRes.value : []);

      const failedRequests = [peticionesRes, tecnicosRes, sucursalesRes, departamentosRes, empleadosRes].filter(result => result.status === 'rejected');
      if (failedRequests.length > 0) {
        showToast('warn', 'Advertencia', `Algunos datos no se pudieron cargar completamente (${failedRequests.length} errores)`, 4000);
      }
    } catch (error) {
      showToast('error', 'Error', 'No se pudieron cargar los datos iniciales');
    } finally {
      setLoading(false);
    }
  }, [fetchWithErrorHandling, showToast]);

  useEffect(() => { cargarDatosIniciales(); }, [cargarDatosIniciales]);

  const filteredPeticiones = useMemo(() => {
    return peticiones.filter(p => {
      const estatusReal = getEstatusReal(p.estatus, !!p.confirmado);
      const searchLower = filterState.searchTerm.toLowerCase();
      
      const matchesSearch = !filterState.searchTerm ||
        p.folio.toString().includes(filterState.searchTerm) ||
        p.titulo.toLowerCase().includes(searchLower) ||
        (p.empleado?.nomemp?.toLowerCase().includes(searchLower) ?? false);
      
      const matchesEstatus = !filterState.estatus || estatusReal === filterState.estatus;
      const matchesSucursal = !filterState.sucursal || p.sucursal === filterState.sucursal;
      const matchesTecnico = !filterState.tecnico ||
        (filterState.tecnico === 'sin_asignar' ? !p.tecnico_id : p.tecnico_id?.toString() === filterState.tecnico);

      return matchesSearch && matchesEstatus && matchesSucursal && matchesTecnico;
    });
  }, [peticiones, filterState]);

  const sucursalOptions = useMemo(() => [
    { label: 'Todas las sucursales', value: '' },
    ...Array.from(new Set(peticiones.map(p => p.sucursal))).filter(Boolean).map(s => ({ label: s, value: s }))
  ], [peticiones]);

  const tecnicoOptions = useMemo(() => [
    { label: 'Todos los técnicos', value: '' },
    { label: 'Sin asignar', value: 'sin_asignar' },
    ...tecnicos.map(t => ({ label: t.nombre, value: t.id.toString() }))
  ], [tecnicos]);

  const empleadosFiltrados = useMemo(() => {
    const searchLower = searchEmpleado.toLowerCase();
    return empleados.filter(emp =>
      emp.cveemp.toLowerCase().includes(searchLower) ||
      emp.nomemp.toLowerCase().includes(searchLower)
    );
  }, [empleados, searchEmpleado]);

  const recargarPeticiones = useCallback(async () => {
    try {
      const peticionesData = await fetchWithErrorHandling(API_URLS.peticiones.obtener);
      setPeticiones(Array.isArray(peticionesData) ? peticionesData : []);
    } catch (error) {
      showToast('error', 'Error', 'No se pudieron recargar las peticiones');
    }
  }, [fetchWithErrorHandling, showToast]);

  const handleSeleccionarPeticion = useCallback((p: PeticionType) => {
    setPeticionSeleccionada(p);
    const estatusReal = getEstatusReal(p.estatus, !!p.confirmado);

    let tecnicoEncontrado: Tecnico | null = null;
    if (p.tecnicoAsignado) {
      tecnicoEncontrado = { id: p.tecnicoAsignado.id, nombre: p.tecnicoAsignado.nombre, usuario: p.tecnicoAsignado.usuario };
    } else if (p.tecnico_id) {
      const tecnicoEnLista = tecnicos.find(t => t.id === p.tecnico_id) || null;
      if (tecnicoEnLista) tecnicoEncontrado = tecnicoEnLista;
    }

    let fechaPromesaString = '';
    if (p.fechapromesa) {
      if (p.fechapromesa.match(/^\d{4}-\d{2}-\d{2}$/)) {
        fechaPromesaString = p.fechapromesa;
      } else {
        try {
          const date = new Date(p.fechapromesa);
          if (!isNaN(date.getTime())) {
            const mazatlanDate = new Date(date.toLocaleString("en-US", {timeZone: MAZATLAN_TIMEZONE}));
            fechaPromesaString = mazatlanDate.toISOString().split('T')[0];
          }
        } catch (error) {
          fechaPromesaString = '';
        }
      }
    }

    updateFormState({
      folio: p.folio.toString(), empleado: p.empleado || null, fechaHora: formatDateTimeFromDB(p.fecha_hora),
      titulo: p.titulo, justificacion: p.justificacion, sucursal: p.sucursal, departamento: p.departamento,
      estatus: estatusReal, confirmado: !!p.confirmado, fechapromesa: fechaPromesaString,
      motivo: p.motivo || '', tecnicoAsignado: tecnicoEncontrado, imagen_url: p.imagen_url || null,
      imagenFile: null, imagenPreview: null
    });
    setMode('view');
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, [tecnicos, updateFormState]);

  const handleLimpiarFormulario = useCallback(() => {
    resetFormState();
    setPeticionSeleccionada(null);
    setMode('view');
    showToast('info', 'Formulario limpiado', 'El formulario ha sido limpiado correctamente', 2000);
  }, [resetFormState, showToast]);

  const validarCampos = useCallback((): boolean => {
    const validations = [
      { condition: !formState.titulo.trim(), message: 'Debe ingresar un título' },
      { condition: !formState.justificacion.trim(), message: 'Debe ingresar una justificación' },
      { condition: !formState.sucursal, message: 'Debe seleccionar una sucursal' },
      { condition: !formState.departamento, message: 'Debe seleccionar un departamento' },
      { condition: !formState.empleado, message: 'Debe seleccionar un empleado' }
    ];

    for (const validation of validations) {
      if (validation.condition) {
        showToast('warn', 'Advertencia', validation.message, 2000);
        return false;
      }
    }
    return true;
  }, [formState, showToast]);

  const obtenerSiguienteFolio = useCallback(async (): Promise<number> => {
    try {
      const peticionesData = await fetchWithErrorHandling(API_URLS.peticiones.obtener);
      const peticionesList: PeticionType[] = Array.isArray(peticionesData) ? peticionesData : [];
      return peticionesList.length === 0 ? 1 : Math.max(...peticionesList.map(p => p.folio)) + 1;
    } catch (error) {
      return 1;
    }
  }, [fetchWithErrorHandling]);

  const prepararNuevaPeticion = useCallback(async () => {
    try {
      const nuevoFolio = await obtenerSiguienteFolio();
      updateFormState({
        folio: nuevoFolio.toString(), 
        empleado: null, 
        fechaHora: getCurrentDateTime(),
        titulo: '', 
        justificacion: '', 
        sucursal: '', 
        departamento: '', 
        estatus: 'PENDIENTE',
        confirmado: false, 
        fechapromesa: '', 
        motivo: '', 
        tecnicoAsignado: null,
        imagen_url: null, 
        imagenFile: null, 
        imagenPreview: null
      });
      setPeticionSeleccionada(null);
      setMode('create');
      return nuevoFolio;
    } catch (error) {
      showToast('error', 'Error', 'No se pudo preparar una nueva petición');
      return null;
    }
  }, [obtenerSiguienteFolio, updateFormState, showToast]);

  const handleCrearNuevaPeticion = useCallback(async () => {
    const nuevoFolio = await prepararNuevaPeticion();
    if (nuevoFolio) {
      showToast('info', 'Nueva Petición', `Creando nueva petición con folio: ${nuevoFolio}`, 2000);
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
  }, [prepararNuevaPeticion, showToast]);

  const handleImagenChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0] || null;
  if (file) {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      showToast('warn', 'Advertencia', 'Solo se permiten imágenes (JPG, PNG, etc.) y archivos PDF');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast('warn', 'Advertencia', 'El archivo no debe superar los 5MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      updateFormState({
        imagenFile: file,
        imagenPreview: ev.target?.result as string
      });
    };
    reader.onerror = () => {
      showToast('error', 'Error', 'No se pudo leer el archivo');
    };
    // Solo generamos preview si es imagen; para PDF no hay preview útil en <img>
    if (file.type.startsWith('image/')) {
      reader.readAsDataURL(file);
    } else {
      // Es PDF: no hay preview visual, pero sí lo guardamos como archivo
      updateFormState({
        imagenFile: file,
        imagenPreview: null // o podrías usar una miniatura genérica de PDF si quieres
      });
    }
  } else {
    updateFormState({ imagenFile: null, imagenPreview: null });
  }
}, [updateFormState, showToast]);

  const handleGuardarNueva = useCallback(async () => {
    if (!validarCampos()) return;

    setSaving(true);
    try {
      let response: Response;
      
      if (formState.imagenFile) {
        const formData = new FormData();
        formData.append('cveemp', formState.empleado?.cveemp || '');
        formData.append('titulo', formState.titulo.trim());
        formData.append('justificacion', formState.justificacion.trim());
        formData.append('sucursal', formState.sucursal);
        formData.append('departamento', formState.departamento);
        formData.append('estatus', 'PENDIENTE');
        formData.append('confirmado', 'false');
        formData.append('fecha_hora', formatDateTimeForDB(formState.fechaHora));
        if (formState.fechapromesa) formData.append('fechapromesa', formState.fechapromesa);
        if (formState.motivo) formData.append('motivo', formState.motivo);
        if (formState.tecnicoAsignado?.id) formData.append('tecnico_id', formState.tecnicoAsignado.id.toString());
        formData.append('imagen', formState.imagenFile);
        // ✅ Indicar que solo se envíe al grupo, no a técnicos individuales
        formData.append('notificar_solo_grupo', 'true');

        response = await fetch(API_URLS.peticiones.registrar, { method: 'POST', body: formData });
      } else {
        const payload = {
          cveemp: formState.empleado?.cveemp || '', titulo: formState.titulo.trim(),
          justificacion: formState.justificacion.trim(), sucursal: formState.sucursal,
          departamento: formState.departamento, estatus: 'PENDIENTE', confirmado: false,
          fecha_hora: formatDateTimeForDB(formState.fechaHora),
          fechapromesa: formState.fechapromesa || null, motivo: formState.motivo || null,
          tecnico_id: formState.tecnicoAsignado?.id || null,
          // ✅ Indicar que solo se envíe al grupo, no a técnicos individuales
          notificar_solo_grupo: true
        };
        
        response = await fetch(API_URLS.peticiones.registrar, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Error ${response.status}: ${errorData}`);
      }
      
      showToast('success', 'Éxito', 'Petición creada exitosamente (notificación enviada solo al grupo)', 2000);
      
      // Recargar peticiones en segundo plano
      recargarPeticiones();
      
      // Preparar inmediatamente una nueva petición para creación rápida
      const nuevoFolio = await prepararNuevaPeticion();
      if (nuevoFolio) {
        showToast('info', 'Lista para nueva petición', `Nueva petición preparada con folio: ${nuevoFolio}`, 2000);
      }
      
    } catch (error) {
      showToast('error', 'Error', 'No se pudo crear la petición');
    } finally {
      setSaving(false);
    }
  }, [formState, validarCampos, recargarPeticiones, prepararNuevaPeticion, showToast]);

  const handleGuardarEdicion = useCallback(async () => {
    if (!formState.folio || !validarCampos()) return;

    setSaving(true);
    const payload = {
      estatus: formState.estatus, confirmado: formState.confirmado,
      fechapromesa: formState.fechapromesa || null, motivo: formState.motivo || null,
      tecnico_id: formState.tecnicoAsignado?.id || null,
      // ✅ Indicar que solo se envíe al grupo, no a técnicos individuales
      notificar_solo_grupo: true
    };

    try {
      const response = await fetch(API_URLS.peticiones.actualizar(formState.folio), {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Error ${response.status}: ${errorData}`);
      }
      
      showToast('success', 'Éxito', 'Petición actualizada exitosamente (notificación enviada solo al grupo)', 2000);
      setMode('view');
      await recargarPeticiones();
    } catch (error) {
      showToast('error', 'Error', 'No se pudo actualizar la petición');
    } finally {
      setSaving(false);
    }
  }, [formState, validarCampos, recargarPeticiones, showToast]);

  const handleEliminar = useCallback(() => {
    if (!formState.folio) {
      showToast('warn', 'Advertencia', 'Seleccione una petición para eliminar', 2000);
      return;
    }
    
    confirmDialog({
      message: `¿Está seguro de eliminar la petición con folio: ${formState.folio}?`,
      header: 'Confirmar eliminación', icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          const response = await fetch(API_URLS.peticiones.eliminar(formState.folio), { method: 'DELETE' });
          if (!response.ok) throw new Error(`Error ${response.status}`);
          showToast('success', 'Éxito', 'Petición eliminada exitosamente', 2000);
          handleLimpiarFormulario();
          await recargarPeticiones();
        } catch (error) {
          showToast('error', 'Error', 'No se pudo eliminar la petición');
        }
      }
    });
  }, [formState.folio, handleLimpiarFormulario, recargarPeticiones, showToast]);

  const handleEditar = useCallback(() => {
    if (!formState.folio) {
      showToast('warn', 'Advertencia', 'Seleccione una petición para editar', 2000);
      return;
    }
    setMode('edit');
  }, [formState.folio, showToast]);

  const handleCancelar = useCallback(() => {
    setMode('view');
    resetFormState();
    setPeticionSeleccionada(null);
  }, [resetFormState]);

  const handleVerImagen = useCallback((folio: number) => {
    // ✅ Corregido: usar la ruta específica para obtener imágenes
    const imagenUrl = API_URLS.peticiones.obtenerImagen(folio.toString());
    setImagenSeleccionada(imagenUrl);
    setModalImagenVisible(true);
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  }, []);

  const imagenBodyTemplate = useCallback((rowData: PeticionType) => (
    <div style={{ textAlign: 'center' }}>
      {rowData.imagen_url ? (
        <Button icon="pi pi-image" className="p-button-rounded p-button-text"
          style={{ color: '#10b981', fontSize: '1.2rem' }}
          onClick={() => handleVerImagen(rowData.folio)}
          tooltip="Ver imagen" tooltipOptions={{ position: 'top' }} />
      ) : (
        <i className="pi pi-minus" style={{ color: '#6b7280', fontSize: '1rem' }} title="Sin imagen" />
      )}
    </div>
  ), [handleVerImagen]);

  const estatusBodyTemplate = useCallback((p: PeticionType) => {
    const estatus = getEstatusReal(p.estatus, !!p.confirmado);
    return (
      <span style={{
        backgroundColor: getEstatusColor(estatus), color: 'white', padding: '4px 8px',
        borderRadius: '12px', fontSize: '11px', fontWeight: 'bold',
        textAlign: 'center', display: 'inline-block', minWidth: '80px'
      }}>
        {estatus}
      </span>
    );
  }, []);

  const tecnicoBodyTemplate = useCallback((rowData: PeticionType) => {
    const tecnico = rowData.tecnicoAsignado;
    const tecnicoId = rowData.tecnico_id;
    
    if (tecnico && tecnico.nombre) {
      return (
        <div style={{ fontSize: '12px' }}>
          <div style={{ fontWeight: 'bold', color: '#495057' }}>{tecnico.nombre}</div>
          <div style={{ color: '#6c757d', fontSize: '11px' }}>({tecnico.usuario || 'N/A'})</div>
        </div>
      );
    }
    
    if (tecnicoId) {
      const tecnicoEncontrado = tecnicos.find(t => t.id === tecnicoId);
      if (tecnicoEncontrado) {
        return (
          <div style={{ fontSize: '12px' }}>
            <div style={{ fontWeight: 'bold', color: '#495057' }}>{tecnicoEncontrado.nombre}</div>
            <div style={{ color: '#6c757d', fontSize: '11px' }}>({tecnicoEncontrado.usuario})</div>
          </div>
        );
      }
    }
    
    return <span style={{ color: '#6c757d', fontStyle: 'italic' }}>Sin asignar</span>;
  }, [tecnicos]);

  const columns = useMemo(() => [
  { field: 'folio', header: 'Folio', sortable: true, style: { width: '80px', textAlign: 'center' as const } },
  { field: 'fecha_hora', header: 'Fecha y Hora', body: (p: PeticionType) => formatDateTime(p.fecha_hora), style: { width: '200px', textAlign: 'center' as const }, sortable: true },
  { field: 'empleado.nomemp', header: 'Empleado', body: (p: PeticionType) => p.empleado?.nomemp || 'N/A', sortable: true },
  { field: 'titulo', header: 'Título', body: (p: PeticionType) => p.titulo || 'N/A', sortable: true, style: { maxWidth: '250px' } },
  { field: 'estatus', header: 'Estatus', body: estatusBodyTemplate, sortable: true, style: { width: '120px', textAlign: 'center' as const } },
  { field: 'tecnico', header: 'Técnico', body: tecnicoBodyTemplate, style: { width: '200px', textAlign: 'justify' as const }, sortable: true },
  { field: 'imagen_url', header: 'Imagen', body: imagenBodyTemplate, style: { width: '80px', textAlign: 'center' as const } }
], [estatusBodyTemplate, tecnicoBodyTemplate, imagenBodyTemplate]);

  const containerStyle = { backgroundColor: 'rgba(255, 255, 255, 0.95)', borderRadius: '12px', padding: '20px', marginBottom: '20px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' };
  const inputStyle = { width: '100%', height: '40px', border: '2px solid #e5e7eb', borderRadius: '8px', padding: '0 12px', fontSize: '1rem', backgroundColor: '#fff' };
  const labelStyle = { display: 'block', fontSize: '0.95rem', fontWeight: '600', marginBottom: '6px', color: '#374151' };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: '#f8f9fa' }}>
        <div style={{ textAlign: 'center' }}>
          <i className="pi pi-spinner pi-spin" style={{ fontSize: '2rem', color: '#3b82f6' }}></i>
          <div style={{ marginTop: '1rem', color: '#6b7280' }}>Cargando datos...</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />
      <Sidebar />
      <Navbar />
      
      <div className="content-wrapper" style={{
        backgroundImage: 'url(/dist/img/FondoLogin.png)', backgroundSize: 'cover',
        backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed', minHeight: '100vh', padding: '20px'
      }}>
        <div style={{ ...containerStyle, marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h1 style={{ margin: 0, color: '#1f2937', fontSize: '2rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '12px' }}>
              🛠️ Administración de Peticiones
            </h1>
            <Button label="➕ Nueva Petición" icon="pi pi-plus" onClick={handleCrearNuevaPeticion}
              disabled={saving}
              style={{
                backgroundColor: saving ? '#9ca3af' : '#3b82f6', border: 'none',
                borderRadius: '8px', padding: '10px 20px', fontWeight: '600',
                opacity: saving ? 0.7 : 1, cursor: saving ? 'not-allowed' : 'pointer'
              }} />
          </div>
          <div style={{ height: '2px', background: 'linear-gradient(90deg, #3b82f6, #10b981)', borderRadius: '1px' }} />
        </div>

        <div style={containerStyle}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            <InputText placeholder="🔍 Buscar por folio, título o empleado..." value={filterState.searchTerm}
              onChange={(e) => updateFilter({ searchTerm: e.target.value })} style={inputStyle} />
            <Dropdown placeholder="📊 Filtrar por estatus" value={filterState.estatus}
              options={[{ label: 'Todos los estatus', value: '' }, ...ESTATUS_OPTIONS]}
              optionLabel="label" optionValue="value"
              onChange={(e) => updateFilter({ estatus: e.value })} style={{ ...inputStyle, height: '40px' }} />
            <Dropdown placeholder="🏢 Filtrar por sucursal" value={filterState.sucursal}
              options={sucursalOptions} optionLabel="label" optionValue="value"
              onChange={(e) => updateFilter({ sucursal: e.value })} style={{ ...inputStyle, height: '40px' }} />
            <Dropdown placeholder="🔧 Filtrar por técnico" value={filterState.tecnico}
              options={tecnicoOptions} optionLabel="label" optionValue="value"
              onChange={(e) => updateFilter({ tecnico: e.value })} style={{ ...inputStyle, height: '40px' }} />
          </div>
          <div style={{ textAlign: 'right' }}>
            <Button label="🧹 Limpiar filtros" onClick={resetFilters} className="p-button-text" style={{ color: '#6b7280' }} />
          </div>
        </div>

        <div style={containerStyle}>
          <DataTable value={filteredPeticiones} paginator rows={10} rowsPerPageOptions={[5, 10, 25, 50]}
            selectionMode="single" onSelectionChange={(e) => handleSeleccionarPeticion(e.value)}
            metaKeySelection={false} rowHover
            emptyMessage="No hay peticiones que coincidan con los filtros aplicados"
            loading={loading} responsiveLayout="scroll" stripedRows style={{ fontSize: '0.9rem' }}>
            {columns.map((col, i) => <Column key={i} {...col} />)}
          </DataTable>
        </div>

        <div ref={formRef} style={{ ...containerStyle, marginTop: '20px', border: '1px solid rgba(255,255,255,0.2)' }}>
          <h3 style={{ margin: '0 0 24px 0', color: '#1f2937', fontSize: '1.3rem', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
            📋 {mode === 'create' ? 'Crear Nueva' : 'Detalles de la'} Petición
            {saving && <i className="pi pi-spinner pi-spin" style={{ fontSize: '1rem', color: '#3b82f6', marginLeft: '8px' }}></i>}
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={labelStyle}>📄 Folio</label>
              <InputText value={formState.folio || ''} disabled style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>👤 Empleado</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <InputText value={formState.empleado?.nomemp || ''} placeholder="Seleccione un empleado"
                  disabled style={{ ...inputStyle, flex: 1 }} />
                <Button icon="pi pi-search" onClick={() => setModalEmpleadoVisible(true)}
                  disabled={mode !== 'create' || saving}
                  style={{
                    height: '40px', width: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: (mode !== 'create' || saving) ? '#9ca3af' : '#3b82f6',
                    opacity: (mode !== 'create' || saving) ? 0.6 : 1, 
                    cursor: (mode !== 'create' || saving) ? 'not-allowed' : 'pointer',
                    border: 'none', borderRadius: '8px'
                  }}
                  tooltip="Buscar empleado" tooltipOptions={{ position: 'top' }} />
              </div>
            </div>
            <div>
              <label style={labelStyle}>📅 Fecha y Hora</label>
              <InputText type="datetime-local" value={formState.fechaHora}
                onChange={(e) => updateFormState({ fechaHora: e.target.value })}
                disabled={mode !== 'create' || saving} style={{ ...inputStyle, height: '40px' }} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={labelStyle}>🏢 Sucursal</label>
              <Dropdown value={formState.sucursal}
                options={sucursales.map(s => ({ label: s.nombre, value: s.nombre }))}
                onChange={(e) => updateFormState({ sucursal: e.value })}
                placeholder="Seleccionar sucursal" style={{ ...inputStyle, height: '40px' }}
                filter filterBy="label" emptyMessage="No hay sucursales disponibles"
                disabled={mode !== 'create' || saving} />
            </div>
            <div>
              <label style={labelStyle}>🏬 Departamento</label>
              <Dropdown value={formState.departamento}
                options={departamentos.map(d => ({ label: d.nombre, value: d.nombre }))}
                onChange={(e) => updateFormState({ departamento: e.value })}
                placeholder="Seleccionar departamento" style={{ ...inputStyle, height: '40px' }}
                filter filterBy="label" emptyMessage="No hay departamentos disponibles"
                disabled={mode !== 'create' || saving} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={labelStyle}>📌 Título</label>
              <InputText value={formState.titulo || ''}
                onChange={(e) => updateFormState({ titulo: e.target.value })}
                disabled={mode !== 'create' || saving} style={inputStyle}
                placeholder="Ingrese el título de la petición" />
            </div>
          </div>
          

          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>📝 Justificación</label>
            <InputTextarea
  value={formState.justificacion || ''}
  onChange={(e) => updateFormState({ justificacion: e.target.value })}
  readOnly={mode !== 'create' || saving}
  rows={4}
  style={{
    width: '100%',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    padding: '12px',
    fontSize: '1rem',
    lineHeight: '1.5',
    resize: 'vertical',
    backgroundColor: (mode !== 'create' || saving) ? '#f8f9fa' : '#fff',
    color: (mode !== 'create' || saving) ? '#6b7280' : '#000',
    cursor: (mode !== 'create' || saving) ? 'default' : 'text',
    overflow: 'auto' 
  }}
  placeholder="Describa detalladamente la justificación de la petición..."
/>
          </div>

          

          {mode === 'create' && (
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>🖼️ Imagen (opcional)</label>
              <input type="file" accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.pdf" onChange={handleImagenChange}
                disabled={mode !== 'create' || saving}
                style={{
                  marginTop: '8px', padding: '8px', border: '2px dashed #e5e7eb',
                  borderRadius: '8px', width: '100%', backgroundColor: '#f9fafb'
                }} />
              {formState.imagenPreview && (
                <div style={{ marginTop: '12px', textAlign: 'center' }}>
                  <img src={formState.imagenPreview} alt="Previsualización"
                    style={{
                      maxWidth: '300px', maxHeight: '200px', borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }} />
                  <div style={{ marginTop: '8px', fontSize: '0.85rem', color: '#6b7280' }}>
                    Previsualización de la imagen seleccionada
                  </div>
                </div>
              )}
            </div>
          )}

          {formState.imagen_url && (
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>🖼️ Imagen Adjunta</label>
              <div style={{
                border: '2px solid #e5e7eb', borderRadius: '8px', padding: '16px',
                backgroundColor: '#f9fafb', textAlign: 'center'
              }}>
                <img
                  src={API_URLS.peticiones.obtenerImagen(formState.folio)} // ✅ Corregido: usar la ruta específica
                  alt="Imagen de la petición"
                  style={{
                    maxWidth: '300px', maxHeight: '200px', borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', cursor: 'pointer'
                  }}
                  onClick={() => handleVerImagen(parseInt(formState.folio))}
                  onError={(e) => { e.currentTarget.src = '/dist/img/no-image.png'; }} />
                <div style={{ marginTop: '8px', fontSize: '0.85rem', color: '#6b7280' }}>
                  Click para ver en tamaño completo
                </div>
              </div>
            </div>
          )}

          <div style={{
            backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '16px',
            border: '1px solid #dee2e6', marginBottom: '20px'
          }}>
            <h4 style={{ margin: '0 0 16px 0', color: '#495057', fontSize: '1.1rem', fontWeight: '600' }}>
              ✏️ Campos Editables
            </h4>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={labelStyle}>🏷️ Estatus</label>
                <Dropdown value={formState.estatus} options={ESTATUS_OPTIONS}
                  onChange={(e) => updateFormState({ estatus: e.value, motivo: '' })}
                  placeholder="Seleccionar estatus" disabled={mode === 'view' || saving}
                  style={{ ...inputStyle, height: '40px' }} />
              </div>
              <div>
                <label style={labelStyle}>🔧 Técnico Asignado</label>
                <Dropdown value={formState.tecnicoAsignado?.id || ''}
                  options={[
                    { label: 'Sin asignar', value: '' },
                    ...tecnicos.map(t => ({ label: t.nombre, value: t.id }))
                  ]}
                  optionLabel="label" optionValue="value"
                  onChange={(e) => {
                    const tecnico = tecnicos.find(t => t.id === e.value) || null;
                    updateFormState({ tecnicoAsignado: tecnico });
                  }}
                  placeholder="Seleccionar técnico" disabled={mode === 'view' || saving}
                  style={{ ...inputStyle, height: '40px' }} filter filterBy="label"
                  emptyMessage="No hay técnicos disponibles" />
              </div>
              <div>
                <label style={labelStyle}>📅 Fecha Promesa</label>
                <InputText type="date" value={formState.fechapromesa?.split('T')[0] || ''}
                  onChange={(e) => updateFormState({ fechapromesa: e.target.value })}
                  disabled={mode === 'view' || saving} style={{ ...inputStyle, height: '40px' }} />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>📌 Motivo</label>
              <InputTextarea value={formState.motivo || ''}
                onChange={(e) => updateFormState({ motivo: e.target.value })}
                disabled={mode === 'view' || saving}
                placeholder="Ingrese el motivo de la decisión o comentarios administrativos..."
                rows={3}
                style={{
                  width: '100%', padding: '12px', border: '2px solid #e5e7eb',
                  borderRadius: '8px', fontSize: '1rem', lineHeight: '1.5',
                  resize: 'vertical', backgroundColor: (mode === 'view' || saving) ? '#f8f9fa' : '#fff'
                }} />
            </div>

            {/* ✅ Información sobre notificaciones */}
            <div style={{ 
              backgroundColor: '#e3f2fd', border: '1px solid #2196f3', borderRadius: '6px', 
              padding: '12px', marginTop: '16px' 
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1976d2' }}>
                <i className="pi pi-info-circle" style={{ fontSize: '1rem' }}></i>
                <span style={{ fontSize: '0.9rem', fontWeight: '600' }}>
                  📱 Las notificaciones de WhatsApp se envían únicamente al grupo, no a técnicos individuales
                </span>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
            {mode === 'view' ? (
              <>
                <Button label="🧹 Limpiar" icon="pi pi-refresh" onClick={handleLimpiarFormulario}
                  disabled={saving}
                  style={{ 
                    backgroundColor: saving ? '#9ca3af' : '#6b7280', 
                    border: 'none', borderRadius: '8px', padding: '10px 20px', fontWeight: '600',
                    opacity: saving ? 0.6 : 1
                  }} />
                <Button label="✏️ Editar" icon="pi pi-pencil" onClick={handleEditar}
                  disabled={!formState.folio || saving}
                  style={{
                    backgroundColor: (!formState.folio || saving) ? '#9ca3af' : '#10b981', border: 'none',
                    borderRadius: '8px', padding: '10px 20px', fontWeight: '600',
                    opacity: (!formState.folio || saving) ? 0.6 : 1
                  }} />
                <Button label="🗑️ Eliminar" icon="pi pi-trash" severity="danger"
                  onClick={handleEliminar} disabled={!formState.folio || saving}
                  style={{
                    backgroundColor: (!formState.folio || saving) ? '#9ca3af' : '#ef4444', border: 'none',
                    borderRadius: '8px', padding: '10px 20px', fontWeight: '600',
                    opacity: (!formState.folio || saving) ? 0.6 : 1
                  }} />
              </>
            ) : (
              <>
                <Button label="↩️ Cancelar" icon="pi pi-times" onClick={handleCancelar}
                  disabled={saving}
                  style={{ 
                    backgroundColor: saving ? '#9ca3af' : '#6b7280', 
                    border: 'none', borderRadius: '8px', padding: '10px 20px', fontWeight: '600',
                    opacity: saving ? 0.6 : 1
                  }} />
                <Button 
                  label={saving ? "⏳ Guardando..." : "✅ Guardar"} 
                  icon={saving ? "pi pi-spinner pi-spin" : "pi pi-save"}
                  onClick={mode === 'create' ? handleGuardarNueva : handleGuardarEdicion}
                  disabled={saving}
                  style={{ 
                    backgroundColor: saving ? '#9ca3af' : '#10b981', 
                    border: 'none', borderRadius: '8px', padding: '10px 20px', fontWeight: '600',
                    opacity: saving ? 0.8 : 1,
                    cursor: saving ? 'not-allowed' : 'pointer'
                  }} />
              </>
            )}
          </div>
        </div>
      </div>

      <Dialog header="Seleccionar Empleado" visible={modalEmpleadoVisible}
        style={{ width: '50vw' }} onHide={() => setModalEmpleadoVisible(false)}>
        <div style={{ marginBottom: '16px' }}>
          <InputText placeholder="Buscar por clave o nombre..." value={searchEmpleado}
            onChange={(e) => setSearchEmpleado(e.target.value)}
            style={{ width: '100%', padding: '10px', marginBottom: '10px' }} />
        </div>
        <DataTable value={empleadosFiltrados} paginator rows={10} selectionMode="single"
          onSelectionChange={(e) => {
            updateFormState({ empleado: e.value });
            setModalEmpleadoVisible(false);
            setSearchEmpleado('');
          }}
          emptyMessage="No se encontraron empleados">
          <Column field="cveemp" header="Clave" sortable style={{ width: '120px' }} />
          <Column field="nomemp" header="Nombre" sortable />
        </DataTable>
      </Dialog>

      <Dialog header="Imagen de la Petición" visible={modalImagenVisible}
        style={{ width: '80vw', maxWidth: '800px' }} onHide={() => setModalImagenVisible(false)}>
        <div style={{ textAlign: 'center', position: 'relative' }}>
          <div style={{ marginBottom: '10px' }}>
            <Button icon="pi pi-search-plus" onClick={() => setZoomLevel(prev => Math.min(prev + 0.25, 3))}
              style={{ marginRight: '5px' }} disabled={zoomLevel >= 3} />
            <Button icon="pi pi-search-minus" onClick={() => setZoomLevel(prev => Math.max(prev - 0.25, 0.5))}
              style={{ marginRight: '5px' }} disabled={zoomLevel <= 0.5} />
            <Button icon="pi pi-refresh" onClick={() => { setZoomLevel(1); setImagePosition({ x: 0, y: 0 }); }}
              label="Reset" />
          </div>
          <div style={{
            overflow: 'hidden', maxHeight: '70vh', border: '1px solid #ddd',
            borderRadius: '8px', cursor: zoomLevel > 1 ? 'move' : 'default'
          }}>
            <img src={imagenSeleccionada} alt="Imagen de la petición"
              style={{
                transform: `scale(${zoomLevel}) translate(${imagePosition.x / zoomLevel}px, ${imagePosition.y / zoomLevel}px)`,
                transition: isDragging ? 'none' : 'transform 0.2s ease',
                maxWidth: '100%', maxHeight: '70vh', borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
              onMouseDown={(e) => {
                if (zoomLevel > 1) {
                  setIsDragging(true);
                  setDragStart({ x: e.clientX - imagePosition.x, y: e.clientY - imagePosition.y });
                }
              }}
              onMouseMove={(e) => {
                if (isDragging && zoomLevel > 1) {
                  setImagePosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
                }
              }}
              onMouseUp={() => setIsDragging(false)}
              onMouseLeave={() => setIsDragging(false)}
              onError={(e) => { e.currentTarget.src = '/dist/img/no-image.png'; }} />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default PeticionesAdmin;